import { connect } from 'react-redux';
import { TenantPriceList } from '../../../../models/Entities/TenantPriceLists/TenantPriceList';
import {
  MessageBarType,
  PrimaryButton,
  Spinner
} from '@fluentui/react';
import {
  Badge,
  Button,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
  Text
} from '@fluentui/react-components';
import {
  EditRegular,
  InfoRegular,
  LockClosedRegular,
  LockOpenRegular,
  ClockRegular
} from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import Moment from 'react-moment';
import { useNavigate } from 'react-router-dom';
import { MessageComponent } from '../../../../commons/component/MessageComponent';
import TenantPriceListFormComponent from '../TenantPriceListForm/TenantPriceListFormComponent';
import { useBoolean } from '@fluentui/react-hooks';

// Functions
import * as ComponentFunctions from './TenantPriceListFunctions';

////i18Next
import { useTranslation, Trans } from 'react-i18next';

// Styles
import './TenantPriceList.css';
import { ConfirmationButtonComponent } from '../../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';

export const TenantPriceListComponent = (props: ComponentFunctions.Props) => {
  const navigate = useNavigate();
  const [selectedTenantPriceList, setSelectedTenantPriceList] =
    useState<TenantPriceList>();

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] =
    useBoolean(false);

  const onViewTenantPriceListEvent = (_list: TenantPriceList) => {
    navigate('/tenantpricelist/' + _list.id);
  };

  //useTranslation()
  const [t] = useTranslation();

  useEffect(() => {
    props.GetAllTenantPriceList();
  }, []);

  // events

  const onAddTenantPriceListEvent = () => {
    setSelectedTenantPriceList(undefined);
    openPanel();
  };

  const onEditTenantPriceListEvent = (PriceList: TenantPriceList) => {
    setSelectedTenantPriceList(PriceList);
    openPanel();
  };

  const onDeleteTenantPriceListEvent = (PriceList: TenantPriceList) => {
    props.DeleteTenantPriceList(PriceList.id);
  };

  // render

  const columns = useMemo((): TableColumnDefinition<TenantPriceList>[] => [
    createTableColumn<TenantPriceList>({
      columnId: 'name',
      renderHeaderCell: () => t('tenantPriceList.name'),
      renderCell: (item) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {item.isBlocked
            ? <LockClosedRegular style={{ color: 'red', flexShrink: 0 }} />
            : <LockOpenRegular style={{ color: 'green', flexShrink: 0 }} />}
          <div>
            <Text weight="semibold">{item.priceList.listName}</Text>
            <br />
            <Text size={200}>
              {ComponentFunctions.getTenantPriceListTypeDescription(item.priceListType)}
            </Text>
          </div>
        </div>
      )
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'status',
      renderHeaderCell: () => t('tenantPriceList.status.label'),
      renderCell: (item) => {
        const status = ComponentFunctions.getStatus(item);
        if (status.status === 'Expired') {
          return (
            <Badge appearance="filled" color="danger" size="small" icon={<ClockRegular />}>
              {t('tenantPriceList.status.expired')}
            </Badge>
          );
        }
        if (status.status === 'Pending') {
          return (
            <Badge appearance="filled" color="warning" size="small">
              {t('tenantPriceList.status.pending')}
            </Badge>
          );
        }
        return (
          <Badge appearance="outline" color="success" size="small">
            {t('tenantPriceList.status.active')}
          </Badge>
        );
      }
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'comment',
      renderHeaderCell: () => t('tenantPriceList.comment'),
      renderCell: (item) => <Text>{item.priceList.comments}</Text>
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'validSince',
      renderHeaderCell: () => t('tenantPriceList.validSince'),
      renderCell: (item) => item.effectiveDateStart
        ? <Moment format="DD/MM/YYYY">{item.effectiveDateStart}</Moment>
        : <span><Trans i18nKey={'tenantPriceList.notDefined'} /></span>
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'validUntil',
      renderHeaderCell: () => t('tenantPriceList.validityUntil'),
      renderCell: (item) => item.effectiveDateEnd
        ? <Moment format="DD/MM/YYYY">{item.effectiveDateEnd}</Moment>
        : <span><Trans i18nKey={'tenantPriceList.notDefined'} /></span>
    }),
    createTableColumn<TenantPriceList>({
      columnId: 'options',
      renderHeaderCell: () => t('tenantPriceList.options'),
      renderCell: (item) => (
        <div className="SectionControls">
          <Button
            appearance="subtle"
            icon={<InfoRegular />}
            title={t('tenantPriceList.buttons.detailButton')}
            aria-label={t('tenantPriceList.buttons.detailButton')}
            onClick={() => onViewTenantPriceListEvent(item)}
          />
          <Button
            appearance="subtle"
            icon={<EditRegular />}
            title={t('tenantPriceList.buttons.editButton')}
            aria-label={t('tenantPriceList.buttons.editButton')}
            onClick={() => onEditTenantPriceListEvent(item)}
          />
          <div className="danger">
            <ConfirmationButtonComponent
              ButtonId={'MedicalCenterPriceListDeleteButton' + item.id}
              ButtonIconName={'Delete'}
              ButtonText={''}
              ButtonCssClass={''}
              ConfirmationButtonText={t('tenantPriceList.buttons.deleteButton')}
              ConfirmationButtonCssClass={'deleteButton'}
              CancelButtonText={t('tenantPriceList.buttons.cancelButton')}
              CancelButtonCssClass={''}
              Title={t('tenantPriceList.buttons.title')}
              Description={t('tenantPriceList.buttons.description')}
              ElementTextHeader={t('tenantPriceList.buttons.priceList')}
              ElementTextInformation={item.priceList.listName}
              onConfirmation={() => onDeleteTenantPriceListEvent(item)}
              onCancel={() => {}}
              isOperationExecuting={props.isDeleting || false}
              isOperationExecutedWithError={props.failOnDeleting || false}
              OperationError={props.error}
            />
          </div>
        </div>
      )
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, props.isDeleting, props.failOnDeleting, props.error]);

  const TenantPriceListGrid = (
    <div
      style={{
        borderRadius: '4px',
        overflow: 'hidden',
        border: '1px solid var(--colorNeutralStroke2)',
        boxShadow: 'var(--shadow4)'
      }}
    >
      <DataGrid
        items={props.TenantPriceLists || []}
        columns={columns}
        getRowId={(item) => item.id.toString()}
        size="small"
        focusMode="composite"
      >
        <DataGridHeader
          style={{
            backgroundColor: 'var(--colorNeutralBackground3)',
            borderBottom: '2px solid var(--colorNeutralStroke1)'
          }}
        >
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell
                style={{ fontWeight: 'var(--fontWeightSemibold)' as any }}
              >
                {renderHeaderCell()}
              </DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<TenantPriceList>>
          {({ item, rowId }) => {
            const items = props.TenantPriceLists || [];
            const isLast = items[items.length - 1]?.id.toString() === rowId;
            const isExpired = ComponentFunctions.getStatus(item).status === 'Expired';
            return (
              <DataGridRow<TenantPriceList>
                key={rowId}
                style={{
                  ...(!isLast && { borderBottom: '1px solid var(--colorNeutralStroke3)' })
                }}
              >
                {(column) => (
                  <DataGridCell
                    style={isExpired && column.columnId === 'status' ? { backgroundColor: 'rgba(220, 38, 38, 0.08)' } : undefined}
                  >
                    {column.renderCell(item)}
                  </DataGridCell>
                )}
              </DataGridRow>
            );
          }}
        </DataGridBody>
      </DataGrid>
    </div>
  );

  const Loading = (
    <div>
      <Spinner
        label={t('tenantPriceList.loading.priceList')}
        ariaLive="assertive"
        labelPosition="top"
      />
    </div>
  );

  const ComponentBar = (
    <div className="SectionControls spaceBetween">
      <PrimaryButton
        onClick={onAddTenantPriceListEvent}
        text={t('tenantPriceList.buttons.addPriceList')}
        iconProps={ComponentFunctions.AddIcon}
        className="actionButton"
      />
    </div>
  );

  const FormContent = (
    <TenantPriceListFormComponent
      isOpen={isOpen}
      openPanel={() => openPanel()}
      dismissPanel={() => {
        setSelectedTenantPriceList(undefined);
        dismissPanel();
      }}
      TenantPriceList={selectedTenantPriceList}
    />
  );

  return (
    <>
      {ComponentBar}
      {props.error && (
        <MessageComponent
          message={t('tenantPriceList.props.errorMessage')}
          subMessage={props.error?.ErrorMessage}
          type={MessageBarType.error}
          itemList={props.error?.Errors}
        />
      )}
      {props.isGettingAll ? (
        Loading
      ) : props.gettingAllSuccess ? (
        TenantPriceListGrid
      ) : (
        <></>
      )}
      {FormContent}
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(TenantPriceListComponent as any);
